'use client';

import { useMemo } from 'react';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import {
  mobileGraphql,
  mobileKeys,
  type MobileConnection,
  type MobileFrontEndCardView,
  type MobileUiConfigArtifact,
  type MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import { buildCardsQuery, mapCardItems, type CardItem } from '../lib/build-cards-query';

export interface CardPage {
  items: CardItem[];
  total: number;
}

/** One page of entity cards. */
export function useCardItems(args: {
  mobileAcquisitionId: number;
  graphqlType: string;
  artifact: MobileUiConfigArtifact;
  uiConfig: MobileUiConfigResponse;
  view: MobileFrontEndCardView;
  offset: number;
  limit: number;
  where: Record<string, unknown> | null;
  /** When false the query stays idle. Defaults to on. */
  active?: boolean;
}): UseQueryResult<CardPage, Error> {
  const { mobileAcquisitionId, graphqlType, artifact, uiConfig, view, offset, limit, where } =
    args;
  const { cellId, accessToken, enabled } = useCellServiceSession();

  const rootQuery = artifact.front_end?.graphql_root_query ?? null;

  const query = useMemo(() => {
    if (!rootQuery) return null;
    return buildCardsQuery({
      rootQuery,
      view,
      artifact,
      mobileAcquisitionId,
      offset,
      limit,
      where,
    });
  }, [rootQuery, view, artifact, mobileAcquisitionId, offset, limit, where]);

  return useQuery<CardPage, Error>({
    queryKey: mobileKeys.artifactList(cellId, mobileAcquisitionId, graphqlType, {
      view: 'cards',
      offset,
      limit,
      where,
    }),
    enabled: enabled && args.active !== false && Boolean(query),
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const data = await mobileGraphql<
        Record<string, MobileConnection<Record<string, unknown>>>
      >({
        cellId: cellId as string,
        accessToken: accessToken as string,
        endpoint: artifact.endpoint,
        query: query as string,
        signal,
      });
      const connection = data[rootQuery as string];
      return {
        items: mapCardItems(connection?.items ?? [], { view, artifact, uiConfig }),
        total: connection?.total ?? 0,
      };
    },
  });
}
